import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { TeamMember } from '@/types/core/team';
import type { Page } from '@/types/shared/page';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { teamQueryKeys } from './query-keys';

/**
 * List team members for a given team.
 * Accepts optional paging args via `opts`.
 */
export function useListTeamMembers(
  tenantId: UUID | undefined,
  teamId: UUID | undefined,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
    enabled?: boolean;
  }
): UseInfiniteQueryResult<InfiniteData<Page<TeamMember>, unknown>, Error> {
  return useInfiniteQuery<
    Page<TeamMember>,
    Error,
    InfiniteData<Page<TeamMember>, unknown>
  >({
    queryKey: teamQueryKeys.memberList(
      tenantId,
      teamId,
      opts?.limit,
      opts?.filter
    ),
    queryFn: (context: QueryFunctionContext) => {
      if (isEmptyUUID(tenantId) || isEmptyUUID(teamId)) {
        throw new Error('Missing tenantId or teamId');
      }
      const pageParam = context.pageParam as string | undefined;
      return teamService.listTeamMembers(tenantId!, teamId!, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    enabled: opts?.enabled ?? (!isEmptyUUID(tenantId) && !isEmptyUUID(teamId)),
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
